import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Eye, EyeSlash, WarningCircle } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

// Animation constants - extracted to prevent inline object recreation
const pageAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

const errorAnimation = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
};

// Background style constant
const backgroundStyle = {
  backgroundImage: `url('https://images.unsplash.com/photo-1639322537228-f710d846310a?w=1920')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (!err.response) {
        setError('Unable to connect to the server. Please check your connection and try again.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="absolute inset-0 opacity-5" style={backgroundStyle} />
      
      <motion.div
        initial={pageAnimation.initial}
        animate={pageAnimation.animate}
        transition={pageAnimation.transition}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-xl border-slate-200/60">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-[#0B2545] rounded-xl flex items-center justify-center">
              <ShieldCheck size={32} weight="duotone" className="text-white" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
              MojoMoney OTP Gateway
            </CardTitle>
            <CardDescription className="text-slate-600">
              Secure authentication for transaction validation
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <motion.div
                  initial={errorAnimation.initial}
                  animate={errorAnimation.animate}
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
                >
                  <WarningCircle size={18} weight="fill" />
                  <span>{error}</span>
                </motion.div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-[#0B2545] hover:bg-[#13315C] text-white font-medium"
                data-testid="login-submit-button"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-500 hover:text-[#0B2545] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
            
            <p className="mt-6 text-center text-sm text-slate-500">
              Banking-grade security for transaction validation
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
